import { Box, BoxProps, ColorOptions, FileSelector, Icon, ModBox, Spacer, Text } from '@jfs/ui-new'
import { ButtonNew } from '../AdminButton'
import { isEmptyObject } from '@jfs/utils'
import { useCallback, useEffect } from 'react'
import { shadow } from 'styled-system'
import XlsxPopulate from 'xlsx-populate'
import { useStores } from '../../data/store'
import { MessageTypes } from '../../utils'
import { FileSelectorInnerComp } from '../FileSelectorInnerComp'

interface AbstractBulkUploadProps {
  apiHandler: { data: any; state: 'pending' | 'fulfilled' | 'rejected' | undefined }
  selctedFileInfo: { fileName: string; data: any[]; keys: string[] }
  innerCompTitle: string
  innnerCompSubtitle: string
  setSelectedFileInfo: any
  expectedFileCollumns: string[]
  sampleFilePath: string
  onConfirmButtonPress: any
  respCollumns: string[]
}

export const AbstractBulkUpload = ({
  apiHandler,
  selctedFileInfo,
  setSelectedFileInfo,
  innerCompTitle,
  innnerCompSubtitle,
  expectedFileCollumns,
  sampleFilePath,
  onConfirmButtonPress,
  respCollumns,
}: AbstractBulkUploadProps) => {
  const { setMessage } = useStores((store) => ({
    setMessage: store.message.setMessage,
  }))

  useEffect(() => {
    if (['fulfilled'].includes(apiHandler.state)) {
      const apiResp: any[] = apiHandler.data
      console.log('apiResp', apiResp)
      XlsxPopulate.fromBlankAsync().then(async (workbook) => {
        const data: any = []
        data.push([...expectedFileCollumns, 'Status', 'Message'])
        for (let i = 0; i < apiResp.length; i++) {
          data.push(respCollumns.map((key) => apiResp[i][key] ?? ''))
        }

        workbook.sheet(0)?.cell('A1').value(data)

        const blob = await workbook.outputAsync({
          type: 'blob',
        })
        var url = window.URL.createObjectURL(blob as Blob)
        var a = document.createElement('a')
        document.body.appendChild(a)
        a.href = url
        a.download = `PackageMappingResult_${selctedFileInfo.fileName}.xlsx`
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      })
    }
    if (['fulfilled', 'rejected'].includes(apiHandler.state)) {
      setSelectedFileInfo({ fileName: undefined, data: [], keys: [] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiHandler.state])

  const onOutlineButtonPress = useCallback(() => {
    setSelectedFileInfo({ fileName: undefined, data: [], keys: [] })
  }, [setSelectedFileInfo])

  const showToast = useCallback(
    (msg: string) => {
      setMessage(msg, MessageTypes.ERROR)
    },
    [setMessage],
  )

  const readExcel = useCallback(
    async (fileChosen, fileName) => {
      const readPromise = new Promise((resolve, reject) => {
        XlsxPopulate.fromDataAsync(fileChosen)
          .then(function (workbook) {
            const sheet = workbook.sheet(0)
            const rows = sheet._rows
            const keys: string[] = []
            const values: any = []
            rows.forEach((row: any, rowIndex: number) => {
              const obj: any = {}
              for (let i = 1; i < row._cells.length; i++) {
                if (rowIndex === 1) {
                  keys.push(`${row._cells[i]?.value() ?? ''}`)
                } else {
                  const key = keys[i - 1]
                  obj[key] = `${String(row._cells[i]?.value() ?? '')?.trim()}`
                }
              }
              !isEmptyObject(obj) && values.push(obj)
            })
            const isExcelInvalid = expectedFileCollumns.some((item) => !keys.includes(item))
            if (!isExcelInvalid && values.length > 0) {
              setSelectedFileInfo({ fileName: fileName, data: values, keys: keys })
              resolve(values)
            } else {
              console.log('in')
              reject(values.length === 0 ? { message: 'No Content' } : { message: 'Invalid File' })
            }
          })
          .catch(() => {
            reject({ message: 'Invalid File' })
          })
      })
      try {
        await readPromise
      } catch (e) {
        showToast(e?.message ?? 'Invalid file')
        setSelectedFileInfo({ fileName: '', data: [], keys: [] })
      }
    },
    [expectedFileCollumns, setSelectedFileInfo, showToast],
  )

  return (
    <Box {...styles.container}>
      <ModBox show={apiHandler.state === 'pending'} {...styles.absContainer} />
      <Box flex={1} justifyContent={'center'} alignItems={'center'}>
        <FileSelector
          innerCompTitle={innerCompTitle}
          innnerCompSubtitle={innnerCompSubtitle}
          onFileSelect={readExcel}
          fileType=".xlsx"
          filename={selctedFileInfo.fileName}
          InnerComp={FileSelectorInnerComp}
          isLoading={apiHandler.state === 'pending'}
          onCancelPress={onOutlineButtonPress}
          showToast={showToast}
          onDismiss={onOutlineButtonPress}
        />

        <Text fontFamily={'jiotype-medium'} fontSize={12} color={'DARK_GREY'}>
          {'Input File format should be as per the Sample File format.'}
        </Text>
        <Spacer size={12} />
        <a href={sampleFilePath} style={{ textDecoration: 'none' }} rel="noopener noreferrer" target="_self">
          <Box flexDirection={'row'} align={'center'} onPress={() => {}} underlayColor={'GRAY_SHADOW'}>
            <Icon id={'IMPORT'} color={'BLUE_SHADE_PRIMARY'} />
            <Spacer size={8} direction={'horizontal'} />
            <Text fontFamily={'jiotype-medium'} fontSize={12} color={'BLUE_SHADE_PRIMARY'}>
              {'Download Sample Bulk Entity File'}
            </Text>
          </Box>
        </a>
        <Spacer size={25} />
      </Box>
      <Box
        borderTopWidth={1}
        borderColor={'BORDER_COLOR'}
        width={'100%'}
        pv={8}
        ph={16}
        alignItems={'flex-end'}
        justifyContent={'flex-end'}>
        <ButtonNew
          title={'Confirm Upload'}
          isLoading={apiHandler.state === 'pending'}
          onPress={onConfirmButtonPress}
          paddingHorizontal={48}
          disabled={!selctedFileInfo.fileName}
          height={32}
        />
      </Box>
    </Box>
  )
}

const styles = {
  container: {
    underlayColor: 'WHITE',
    overflow: 'auto',
    bg: 'WHITE',
    minHeight: 500,
    backgroundColor: 'WHITE',
    minWidth: 300,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: '#e3e6f0' as ColorOptions,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    ...shadow,
  } as BoxProps,
  absContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    onPress: () => {},
  } as BoxProps,
}
